/**
 * 5-blok — tizimni 1–5 yulduz bilan baholash va fikr qoldirish. Umumiy baho
 * va baholar soni ochiq ko'rsatiladi (`GET /portal/public/feedback/summary/`).
 * Baho avtorizatsiyasiz beriladi, bir brauzerdan kuniga bir marta (backend
 * anonim xesh bo'yicha cheklaydi).
 */
import { useCallback, useEffect, useState } from "react";
import type { CSSProperties, FormEvent } from "react";

import { ApiError } from "@/shared/api/client";
import { portalApi } from "@/shared/api/endpoints";
import type { FeedbackSummary } from "@/shared/api/types";
import { useI18n } from "@/shared/i18n";
import { formatNumber } from "@/shared/lib/format";
import { Icon } from "@/shared/ui/Icon";

const VOTED_KEY = "sf_home_rating_voted";
const today = () => new Date().toISOString().slice(0, 10);

function readVoted(): boolean {
  try {
    return window.localStorage.getItem(VOTED_KEY) === today();
  } catch {
    return false;
  }
}

/** Kasr qiymatli yulduzlar qatori (masalan 4,6 → 4 ta to'liq + 60% beshinchi). */
export function StarRow({ value, size = 20 }: { value: number; size?: number }) {
  const percent = Math.max(0, Math.min(100, (value / 5) * 100));
  const row = [1, 2, 3, 4, 5].map((n) => <Icon key={n} name="star" size={size} strokeWidth={1.5} />);
  return (
    <span className="stars" role="img" aria-label={`${value} / 5`}>
      <span className="stars-base">{row}</span>
      <span className="stars-fill" style={{ width: `${percent}%` } as CSSProperties}>
        {row}
      </span>
    </span>
  );
}

export function RatingSection() {
  const { t, locale } = useI18n();
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [voted, setVoted] = useState(readVoted);

  const load = useCallback(() => {
    portalApi
      .feedbackSummary()
      .then(setSummary)
      .catch(() => undefined);
  }, []);
  useEffect(load, [load]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!stars) {
      setError(t("rating.pick"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await portalApi.feedback({ stars, comment: comment.trim(), page: "home-rating" });
      setMessage(result.duplicate ? t("rating.already") : t("rating.thanks"));
      try {
        window.localStorage.setItem(VOTED_KEY, today());
      } catch {
        /* saqlab bo'lmasa — backend baribir takroriy ovozni cheklaydi */
      }
      setVoted(true);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.isOffline
            ? "Server bilan aloqa yo'q — bahoni keyinroq yuboring."
            : caught.status === 429
              ? "Juda ko'p urinish. Birozdan so'ng qayta urinib ko'ring."
              : caught.message
          : "Kutilmagan xatolik.",
      );
    } finally {
      setBusy(false);
    }
  }

  const count = summary?.count ?? 0;
  const shown = hover || stars;

  return (
    <section className="section home-rating" id="baholash" aria-labelledby="rating-title">
      <div className="container">
        <div className="rating-card" data-reveal>
          <div className="rating-summary">
            <span className="eyebrow">{t("rating.average")}</span>
            {count > 0 ? (
              <>
                <p className="rating-avg">
                  <b className="num">{formatNumber(summary?.average ?? 0, locale, 1)}</b>
                  <span>/ 5</span>
                </p>
                <StarRow value={summary?.average ?? 0} size={22} />
                <p className="rating-count">
                  {formatNumber(count, locale)} {t("rating.count")}
                </p>
                <ul className="rating-bars" aria-hidden="true">
                  {[5, 4, 3, 2, 1].map((n) => {
                    const part = summary?.distribution?.[String(n)] ?? 0;
                    return (
                      <li key={n}>
                        <span className="num">{n}</span>
                        <Icon name="star" size={12} />
                        <span className="rating-bar">
                          <span style={{ width: `${count ? (part / count) * 100 : 0}%` }} />
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <>
                <StarRow value={0} size={22} />
                <p className="rating-count">{t("rating.none")}</p>
              </>
            )}
          </div>

          <div className="rating-form">
            <h2 id="rating-title">{t("rating.title")}</h2>
            <p className="section-lead">{t("rating.lead")}</p>

            {voted || message ? (
              <p className="alert alert-success" role="status">
                <Icon name="checkCircle" size={18} />
                <span>{message || t("rating.already")}</span>
              </p>
            ) : (
              <form onSubmit={submit}>
                <fieldset className="star-pick" onMouseLeave={() => setHover(0)}>
                  <legend className="visually-hidden">{t("rating.title")}</legend>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <label
                      key={n}
                      className={`star-opt${n <= shown ? " is-on" : ""}`}
                      onMouseEnter={() => setHover(n)}
                    >
                      <input
                        type="radio"
                        name="stars"
                        value={n}
                        checked={stars === n}
                        onChange={() => {
                          setStars(n);
                          setError("");
                        }}
                      />
                      <Icon name="star" size={30} strokeWidth={1.4} />
                      <span className="visually-hidden">{`${n} / 5`}</span>
                    </label>
                  ))}
                </fieldset>

                <span className="field">
                  <label className="label" htmlFor="rating-comment">
                    {t("rating.comment")}
                  </label>
                  <textarea
                    id="rating-comment"
                    className="input"
                    rows={2}
                    maxLength={500}
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    placeholder={t("rating.placeholder")}
                  />
                </span>

                {error ? (
                  <p className="field-error" role="alert">
                    <Icon name="alert" size={14} />
                    {error}
                  </p>
                ) : null}

                <button type="submit" className="btn btn-primary" disabled={busy}>
                  <Icon name="send" size={16} />
                  {t("rating.send")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
